import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { can } from '@wapharma/domain';
import { Button, Group, InlineError, Stack, Text } from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { apiGet, apiPut } from '../api';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  EMPTY_LEGAL_IDENTITY,
  LegalIdentityFields,
  legalIdentityFromRecord,
  legalIdentityPayload,
  type LegalIdentityValue,
} from './legal/LegalIdentityFields';

export type OrganizationLegalIdentity = {
  slug: string;
  name: string;
  legalName: string | null;
  addressLine: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  registrations: unknown;
  representatives?: unknown;
  rcNumber: string | null;
  ice: string | null;
  taxId: string | null;
};

/** Fired on window after a save; `detail` is the saved row. */
export const ORGANIZATION_LEGAL_IDENTITY_SAVED_EVENT = 'wapharma-organization-legal-identity-saved';

export type OrganizationLegalIdentityHandle = {
  save: () => Promise<boolean>;
};

export const OrganizationLegalIdentityForm = forwardRef<
  OrganizationLegalIdentityHandle,
  { hideSaveButton?: boolean; quietToast?: boolean }
>(function OrganizationLegalIdentityForm({ hideSaveButton = false, quietToast = false }, ref) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const token = getToken() ?? undefined;
  const canEdit = can(user?.grants, user?.roles ?? [], 'settings', 'edit', user?.packs);
  const [orgName, setOrgName] = useState('');
  const [draft, setDraft] = useState<LegalIdentityValue>(EMPTY_LEGAL_IDENTITY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ data: OrganizationLegalIdentity }>(
        '/admin/organization/wapharma',
        token,
      );
      const row = unwrapData(res) as OrganizationLegalIdentity;
      setOrgName(row.name ?? '');
      setDraft(legalIdentityFromRecord(row));
    } catch (e) {
      setError(getDisplayError(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(async (): Promise<boolean> => {
    if (!token || !canEdit) return true;
    setSaving(true);
    setError(null);
    try {
      const res = await apiPut<{ data: OrganizationLegalIdentity }>(
        '/admin/organization/wapharma',
        legalIdentityPayload(draft, { includeRepresentatives: true }),
        token,
      );
      const row = unwrapData(res) as OrganizationLegalIdentity;
      setOrgName(row.name ?? '');
      setDraft(legalIdentityFromRecord(row));
      window.dispatchEvent(
        new CustomEvent<OrganizationLegalIdentity>(ORGANIZATION_LEGAL_IDENTITY_SAVED_EVENT, {
          detail: row,
        }),
      );
      if (!quietToast) showToast('Identité légale enregistrée.', { tone: 'success' });
      return true;
    } catch (e) {
      setError(getDisplayError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [canEdit, draft, quietToast, showToast, token]);

  useImperativeHandle(ref, () => ({ save: persist }), [persist]);

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Utilisée pour remplir le bloc WaPharma des conventions (société et représentant). Les
        numéros affichés dépendent du pays.
      </Text>
      {error ? <InlineError>{error}</InlineError> : null}
      <LegalIdentityFields
        value={draft}
        onChange={setDraft}
        disabled={!canEdit || loading}
        legalNamePlaceholder={orgName || undefined}
        showRepresentatives
      />
      {canEdit && !hideSaveButton ? (
        <Group justify="flex-end">
          <Button
            type="button"
            variant="soft"
            size="sm"
            loading={saving}
            disabled={loading}
            onClick={() => void persist()}
          >
            Enregistrer
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
});
